import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Camera,
  X,
  ArrowRight,
  Square,
  Type,
  Undo2,
  Redo2,
  Check,
  Palette,
} from 'lucide-react'

interface ScreenCaptureProps {
  onCapture: (blob: Blob, fileName: string) => void
  onCancel: () => void
}

type Tool = 'arrow' | 'rectangle' | 'text'

interface Annotation {
  type: Tool
  color: string
  // Arrow: start and end points
  startX?: number
  startY?: number
  endX?: number
  endY?: number
  // Rectangle: position and size
  x?: number
  y?: number
  width?: number
  height?: number
  // Text
  text?: string
}

const COLORS = [
  { name: 'Red', value: '#EF4444' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Black', value: '#000000' },
]

export default function ScreenCapture({ onCapture, onCancel }: ScreenCaptureProps) {
  const [step, setStep] = useState<'prompt' | 'annotate'>('prompt')
  const [capturing, setCapturing] = useState(false)
  const [error, setError] = useState('')

  // Image state
  const [imageData, setImageData] = useState<string | null>(null)
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })

  // Annotation state
  const [tool, setTool] = useState<Tool>('arrow')
  const [color, setColor] = useState(COLORS[0].value)
  const [annotations, setAnnotations] = useState<Annotation[]>([])
  const [undoStack, setUndoStack] = useState<Annotation[][]>([])
  const [redoStack, setRedoStack] = useState<Annotation[][]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null)
  const [textInput, setTextInput] = useState('')
  const [textPosition, setTextPosition] = useState<{ x: number; y: number } | null>(null)

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Capture screenshot using getDisplayMedia
  const captureScreen = async () => {
    setCapturing(true)
    setError('')

    try {
      // Request screen capture
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      })

      // Get video track
      const track = stream.getVideoTracks()[0]

      // Create video element to capture frame
      const video = document.createElement('video')
      video.srcObject = stream
      await video.play()

      // Wait a moment for video to stabilize
      await new Promise(resolve => setTimeout(resolve, 100))

      // Create canvas and capture frame
      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Could not get canvas context')

      ctx.drawImage(video, 0, 0)

      // Stop the stream immediately after capture
      track.stop()
      stream.getTracks().forEach(t => t.stop())

      // Get image data
      const dataUrl = canvas.toDataURL('image/png')
      setImageData(dataUrl)
      setImageSize({ width: video.videoWidth, height: video.videoHeight })
      setStep('annotate')

    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Screen capture permission denied. Please allow access to capture your screen.')
        } else {
          setError(`Capture failed: ${err.message}`)
        }
      } else {
        setError('Screen capture failed. Please try again.')
      }
    } finally {
      setCapturing(false)
    }
  }

  // Draw annotations on canvas
  const drawAnnotations = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas || !imageData) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Load and draw base image
    const img = new Image()
    img.onload = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // Draw base image
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

      // Draw all annotations
      const allAnnotations = currentAnnotation
        ? [...annotations, currentAnnotation]
        : annotations

      allAnnotations.forEach(ann => {
        ctx.strokeStyle = ann.color
        ctx.fillStyle = ann.color
        ctx.lineWidth = 3
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        if (ann.type === 'arrow' && ann.startX !== undefined && ann.endX !== undefined) {
          // Draw arrow line
          ctx.beginPath()
          ctx.moveTo(ann.startX, ann.startY!)
          ctx.lineTo(ann.endX, ann.endY!)
          ctx.stroke()

          // Draw arrowhead
          const angle = Math.atan2(ann.endY! - ann.startY!, ann.endX - ann.startX!)
          const headLength = 15
          ctx.beginPath()
          ctx.moveTo(ann.endX, ann.endY!)
          ctx.lineTo(
            ann.endX - headLength * Math.cos(angle - Math.PI / 6),
            ann.endY! - headLength * Math.sin(angle - Math.PI / 6)
          )
          ctx.moveTo(ann.endX, ann.endY!)
          ctx.lineTo(
            ann.endX - headLength * Math.cos(angle + Math.PI / 6),
            ann.endY! - headLength * Math.sin(angle + Math.PI / 6)
          )
          ctx.stroke()
        }

        if (ann.type === 'rectangle' && ann.x !== undefined && ann.width !== undefined) {
          ctx.strokeRect(ann.x, ann.y!, ann.width, ann.height!)
        }

        if (ann.type === 'text' && ann.text && ann.x !== undefined) {
          ctx.font = 'bold 18px sans-serif'
          // Draw text background
          const metrics = ctx.measureText(ann.text)
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)'
          ctx.fillRect(ann.x - 2, ann.y! - 18, metrics.width + 4, 24)
          // Draw text
          ctx.fillStyle = ann.color
          ctx.fillText(ann.text, ann.x, ann.y!)
        }
      })
    }
    img.src = imageData
  }, [imageData, annotations, currentAnnotation])

  // Redraw when annotations change
  useEffect(() => {
    drawAnnotations()
  }, [drawAnnotations])

  // Get mouse position relative to canvas
  const getMousePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }

  // Mouse handlers for drawing
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const pos = getMousePos(e)

    if (tool === 'text') {
      setTextPosition(pos)
      return
    }

    setIsDrawing(true)

    if (tool === 'arrow') {
      setCurrentAnnotation({
        type: 'arrow',
        color,
        startX: pos.x,
        startY: pos.y,
        endX: pos.x,
        endY: pos.y,
      })
    } else if (tool === 'rectangle') {
      setCurrentAnnotation({
        type: 'rectangle',
        color,
        x: pos.x,
        y: pos.y,
        width: 0,
        height: 0,
      })
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentAnnotation) return

    const pos = getMousePos(e)

    if (currentAnnotation.type === 'arrow') {
      setCurrentAnnotation({
        ...currentAnnotation,
        endX: pos.x,
        endY: pos.y,
      })
    } else if (currentAnnotation.type === 'rectangle') {
      setCurrentAnnotation({
        ...currentAnnotation,
        width: pos.x - currentAnnotation.x!,
        height: pos.y - currentAnnotation.y!,
      })
    }
  }

  const handleMouseUp = () => {
    if (!isDrawing || !currentAnnotation) return

    setIsDrawing(false)

    // Only add if it has some size
    const hasSize = currentAnnotation.type === 'arrow'
      ? Math.abs(currentAnnotation.endX! - currentAnnotation.startX!) > 5 ||
        Math.abs(currentAnnotation.endY! - currentAnnotation.startY!) > 5
      : Math.abs(currentAnnotation.width!) > 5 || Math.abs(currentAnnotation.height!) > 5

    if (hasSize) {
      setUndoStack([...undoStack, annotations])
      setRedoStack([])
      setAnnotations([...annotations, currentAnnotation])
    }

    setCurrentAnnotation(null)
  }

  // Add text annotation
  const addTextAnnotation = () => {
    if (!textPosition || !textInput.trim()) return

    setUndoStack([...undoStack, annotations])
    setRedoStack([])
    setAnnotations([...annotations, {
      type: 'text',
      color,
      x: textPosition.x,
      y: textPosition.y,
      text: textInput.trim(),
    }])

    setTextPosition(null)
    setTextInput('')
  }

  // Undo/Redo
  const handleUndo = () => {
    if (undoStack.length === 0) return

    const previous = undoStack[undoStack.length - 1]
    setUndoStack(undoStack.slice(0, -1))
    setRedoStack([...redoStack, annotations])
    setAnnotations(previous)
  }

  const handleRedo = () => {
    if (redoStack.length === 0) return

    const next = redoStack[redoStack.length - 1]
    setRedoStack(redoStack.slice(0, -1))
    setUndoStack([...undoStack, annotations])
    setAnnotations(next)
  }

  // Save screenshot
  const handleSave = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    canvas.toBlob((blob) => {
      if (blob) {
        const fileName = `screenshot-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.png`
        onCapture(blob, fileName)
      }
    }, 'image/png')
  }

  // Prompt step - ask user to capture
  if (step === 'prompt') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Capture Screenshot</h2>
            <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <p className="text-gray-600 mb-6">
            Click the button below to capture your screen, a window, or a browser tab.
            You'll be able to annotate the screenshot before saving.
          </p>

          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              onClick={captureScreen}
              disabled={capturing}
              className="btn-primary flex-1"
            >
              {capturing ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Capturing...
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  Capture Screen
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Annotation step
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-gray-800 text-white">
        <div className="flex items-center gap-4">
          <h2 className="font-semibold">Annotate Screenshot</h2>

          {/* Tool selector */}
          <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setTool('arrow')}
              className={`p-2 rounded ${tool === 'arrow' ? 'bg-primary-600' : 'hover:bg-gray-600'}`}
              title="Arrow"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setTool('rectangle')}
              className={`p-2 rounded ${tool === 'rectangle' ? 'bg-primary-600' : 'hover:bg-gray-600'}`}
              title="Rectangle"
            >
              <Square className="h-4 w-4" />
            </button>
            <button
              onClick={() => setTool('text')}
              className={`p-2 rounded ${tool === 'text' ? 'bg-primary-600' : 'hover:bg-gray-600'}`}
              title="Text"
            >
              <Type className="h-4 w-4" />
            </button>
          </div>

          {/* Color picker */}
          <div className="flex items-center gap-1 bg-gray-700 rounded-lg p-1">
            <Palette className="h-4 w-4 mx-1 text-gray-400" />
            {COLORS.map(c => (
              <button
                key={c.value}
                onClick={() => setColor(c.value)}
                className={`w-6 h-6 rounded ${color === c.value ? 'ring-2 ring-white ring-offset-1 ring-offset-gray-700' : ''}`}
                style={{ backgroundColor: c.value }}
                title={c.name}
              />
            ))}
          </div>

          {/* Undo/Redo */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className="p-2 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Undo"
            >
              <Undo2 className="h-4 w-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className="p-2 hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Redo"
            >
              <Redo2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="btn-secondary text-sm">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary text-sm">
            <Check className="h-4 w-4 mr-1" />
            Save Screenshot
          </button>
        </div>
      </div>

      {/* Canvas area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto p-4 flex items-center justify-center"
      >
        <canvas
          ref={canvasRef}
          width={imageSize.width}
          height={imageSize.height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className="max-w-full max-h-full shadow-xl cursor-crosshair"
          style={{
            maxWidth: '100%',
            maxHeight: 'calc(100vh - 120px)',
            objectFit: 'contain',
          }}
        />
      </div>

      {/* Text input modal */}
      {textPosition && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-lg shadow-xl p-4 w-80">
            <label className="block text-sm font-medium mb-2">Enter text</label>
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addTextAnnotation()
                if (e.key === 'Escape') {
                  setTextPosition(null)
                  setTextInput('')
                }
              }}
              className="input w-full mb-3"
              autoFocus
              placeholder="Type your annotation..."
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setTextPosition(null)
                  setTextInput('')
                }}
                className="btn-secondary text-sm"
              >
                Cancel
              </button>
              <button
                onClick={addTextAnnotation}
                disabled={!textInput.trim()}
                className="btn-primary text-sm"
              >
                Add Text
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
